import { observer } from "mobx-react-lite";
import React, { ReactNode, useContext } from "react";
import {
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  useHistory,
  useRouteMatch
} from "react-router";
import {
  ListSchema,
  CreateListSchema,
  UpdateListSchema,
  ReadonlyUpdateListSchema,
  customerListsContext,
  listContext
} from "./schema";
import {
  CreateView,
  IndexView,
  ShowView,
  withBreadCrumbs
} from "lego-gui-lib/lib";
import { listEntryContext, ListEntrySchemaColumns } from "./listEntry/schema";
import { CreateListEntry, ShowListEntry } from "./listEntry/views";
import i18next from "i18next";
import { CustomerAwareRowButtons } from "../../common/CustomerAwareRowButtons";
import { ParentRoute } from "../../core/model/parent";
import { isAllowed } from "../../common/common";
import * as queryString from "querystring";
import { RelativeButtonLink, Secured } from "lego-gui-lib";
import AddIcon from "@material-ui/icons/Add";
import UploadButton from "./listEntry/UploadButton";
import { authStoreContext } from "../../core/mainStore";
import { useTranslation } from "react-i18next";
import { authSchemaHelper } from "../../core/model/authSchemaHelper";

export const List = {
  url: "/lists",
  handler: (props: RouteComponentProps<any>) => {
    const baseUrl = props.match.url;
    const title = i18next.t("hns:list:domain_plural");
    return withBreadCrumbs(
      { to: baseUrl, children: title },
      <>
        <ParentRoute path={baseUrl + "/:id"} dataSourceName={"list"} />
        <Switch>
          <Route exact={true} path={baseUrl} component={Index} />
          <Route
            path={baseUrl + "/:id/entries/create/:entryId"}
            component={CreateListEntry}
          />
          <Route
            path={baseUrl + "/:id/entries/create"}
            component={CreateListEntry}
          />
          <Route
            path={baseUrl + "/:id/entries/:entryId"}
            component={ShowListEntry}
          />
          <Redirect from={baseUrl + "/:id/entries"} to={baseUrl + "/:id"} />
          <Route path={baseUrl + "/create/:id"} component={Clone} />
          <Route path={baseUrl + "/create"} component={Create} />
          <Route path={baseUrl + "/:id"} component={Show} />
        </Switch>
      </>
    );
  }
};

export const Index = observer(({ ...rest }: any) => {
  const i18n = useTranslation();
  const title = i18n.t("hns:list:domain", { count: 2 });

  const list = useContext(listContext);
  const customerLists = useContext(customerListsContext);
  const auth = useContext(authStoreContext);

  return (
    <IndexView
      dataSource={
        auth.isAnyCustomerOwner() ? customerLists : list.listDataSource
      }
      {...rest}
      title={title}
      toolbarButtons={{ delete: true, export: true }}
      rowButtons={CustomerAwareRowButtons}
      columns={ListSchema}
    />
  );
});

export const Create = observer(({ ...rest }: any) => {
  const i18n = useTranslation();
  const title = i18n.t("common:create", { domain: "hns:list:domain" });

  const list = useContext(listContext);
  const match = useRouteMatch();
  const history = useHistory();

  return withBreadCrumbs(
    { to: match.url + "/create", children: title },
    <CreateView
      dataSource={list.oneDataSource}
      {...rest}
      title={title}
      initialValues={authSchemaHelper.prepareInitialValuesForAnyCustomerOwnerCreate(
        list
      )}
      fields={CreateListSchema}
      id={match.params}
      goBack={() => history.goBack()}
    />
  );
});

const toolbarButtons = (allowed: boolean) => {
  return allowed
    ? { delete: true, export: true }
    : { delete: false, export: true };
};

const rowButtons = (allowed: boolean) => {
  return allowed
    ? { edit: true, delete: true, copy: true, export: true }
    : { edit: false, delete: false, copy: false, export: false };
};

export const titleButtons = (allowed: boolean) => (props: any): ReactNode => {
  let createInitialValuesQuery;
  const urlPrefix = props.baseUrl || "";

  const executeUpload = (event: any) => {
    props.dataSource
      .uploadFileWithEntries(event.target.files[0])
      .then((r: any) => {
        props.dataSource.findAll();
      });
  };

  if (allowed) {
    if (props.fixedParams) {
      const createInitialValues = props.fixedParams.filters.reduce(
        (acc: any, field: any) => {
          acc[field.field] = field.value;
          return acc;
        },
        {}
      );
      createInitialValuesQuery = queryString.stringify(createInitialValues);
    } else {
      createInitialValuesQuery = "";
    }

    return (
      <Secured>
        <RelativeButtonLink
          to={urlPrefix + "/create?" + createInitialValuesQuery}
          variant="contained"
          color="primary"
          icon={<AddIcon />}
        />
        <UploadButton onSelected={executeUpload} />
      </Secured>
    );
  } else {
    return <></>;
  }
};

export const Show = observer(({ ...rest }: any) => {
  const match = useRouteMatch();
  const list = useContext(listContext);
  const listEntry = useContext(listEntryContext);

  const allowed = isAllowed(list);
  const i18n = useTranslation();
  const title = allowed
    ? i18n.t("common:edit", { domain: "hns:list:domain" })
    : i18n.t("hns:list:domain");
  const indexTitle = i18n.t("hns:listEntry:domain_plural");
  const history = useHistory();

  return withBreadCrumbs(
    { to: match.url + "/edit", children: title },
    <>
      <ShowView
        dataSource={list.oneDataSource}
        hideDeleteButton={!allowed}
        preloadData={false}
        title={title}
        fields={!allowed ? ReadonlyUpdateListSchema : UpdateListSchema}
        id={match.params}
        onlyView={!allowed}
        goBack={() => history.goBack()}
      />
      <IndexView
        baseUrl={"/entries"}
        dataSource={listEntry.listDataSource}
        {...rest}
        titleButtons={titleButtons(allowed)}
        toolbarButtons={toolbarButtons(allowed)}
        rowButtons={rowButtons(allowed)}
        title={indexTitle}
        columns={ListEntrySchemaColumns}
      />
    </>
  );
});

export const Clone = observer(({ ...rest }: any) => {
  const match = useRouteMatch();
  const list = useContext(listContext);
  const history = useHistory();
  const i18n = useTranslation();
  const title = i18n.t("common:create", {
    domain: "hns:list:domain"
  });

  return withBreadCrumbs(
    { to: match.url + "/edit", children: i18n.t("common:copy") },
    <CreateView
      submit={async () => {
        return list.oneDataSource.clone();
      }}
      dataSource={list.oneDataSource}
      {...rest}
      title={title}
      fields={CreateListSchema}
      id={match.params}
      initialValues={authSchemaHelper.prepareInitialValuesForAnyCustomerOwnerCreate(
        list
      )}
      goBack={() => history.goBack()}
    />
  );
});
